import React, { useEffect, useMemo, useState } from "react";
import { Alert, CircularProgress, Grid, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DevopsHubHeader from "../components/devops/DevopsHubHeader";
import DevopsEntityCard from "../components/devops/DevopsEntityCard";
import { devopsService } from "../api/services/devopsService";
import { useAuth } from "../contexts/AuthContext";
import { useDevopsWorkspace } from "../contexts/DevopsWorkspaceContext";
import type { DevopsRelease } from "../types/devops";
import { canMaintainRelease } from "../utils/devopsAccess";
import { devopsRoutes } from "../utils/devopsRoutes";

const ReleasesHubPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const roles = user?.roles ?? [];
  const { bindEntity } = useDevopsWorkspace();
  const [items, setItems] = useState<DevopsRelease[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const canWrite = canMaintainRelease(roles);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const releases = await devopsService.listReleases();
        if (!cancelled) setItems(releases);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load releases");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) =>
      [item.name, item.version, item.releaseStatus, item.environment.name, item.owner]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [items, search]);

  return (
    <>
      <DevopsHubHeader
        title="Releases"
        description="A release belongs to exactly one environment. Linked apps and configs must already belong to that environment. DevOps operators own writes."
        search={search}
        onSearchChange={setSearch}
        onAdd={canWrite ? () => navigate(devopsRoutes.releaseNew) : undefined}
        addLabel="Add release"
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Grid container spacing={2}>
          {filtered.map((item) => (
            <Grid item xs={12} sm={6} key={item.uuid}>
              <DevopsEntityCard
                title={`${item.name} ${item.version}`}
                subtitle={`${item.environment.name} · ${item.releaseStatus}`}
                status={item.status}
                metadata={
                  <Typography variant="caption" color="text.secondary">
                    Owner {item.owner} · {(item.applications ?? []).length} app
                    {(item.applications ?? []).length === 1 ? "" : "s"}
                  </Typography>
                }
                onView={() => navigate(devopsRoutes.releaseView(item.uuid))}
                onAskAgent={() =>
                  bindEntity({ type: "RELEASE", uuid: item.uuid, name: item.name })
                }
                onEdit={canWrite ? () => navigate(devopsRoutes.releaseEdit(item.uuid)) : undefined}
                onDelete={
                  canWrite
                    ? async () => {
                        if (!window.confirm(`Delete release ${item.name} ${item.version}?`)) return;
                        await devopsService.deleteRelease(item.uuid);
                        setItems((prev) => prev.filter((row) => row.uuid !== item.uuid));
                      }
                    : undefined
                }
              />
            </Grid>
          ))}
          {!loading && filtered.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No releases match your search.
              </Typography>
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};

export default ReleasesHubPage;
